import React, { useState, useEffect } from 'react';
import PageTabs from '../shared/PageTabs';
import { SETTINGS_TABS } from '../shared/tabConfigs';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider
} from '@mui/material';
import { ContentCopy, Security, Delete } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useToast } from '../../context/ToastContext';
import { settingsService } from '../../services/api';
import { tokens } from '../../theme';
import StatusPill from '../shared/StatusPill';

const T = tokens;
const CodeBlock = styled('pre')(({ theme }) => ({
  backgroundColor: '#0F0F12',
  color:'#E2E8F0',
  padding: '16px 20px',
  overflow: 'auto',
  fontSize: '0.875rem',
  fontFamily: '"Fira Code", "Fira Mono", monospace',
  borderRadius: T.radius.md,
  //border: `1px solid ${theme.palette.grey[300]}`,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all'
}));

const EmbedSettings = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [embedEnabled, setEmbedEnabled] = useState(false);
  const [embedToken, setEmbedToken] = useState(null);
  const [allowedDomain, setAllowedDomain] = useState('');
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  
  // Settings state
  const [myLearningTypeFilter, setMyLearningTypeFilter] = useState(true);
  const [myLearningSortingFilter, setMyLearningSortingFilter] = useState(true);
  const [myLearningGridListButtons, setMyLearningGridListButtons] = useState(true);
  const [catalogTypeFilter, setCatalogTypeFilter] = useState(true);
  const [catalogSortingFilter, setCatalogSortingFilter] = useState(true);
  const [catalogGridListButtons, setCatalogGridListButtons] = useState(true);

  useEffect(() => {
    fetchEmbedSettings();
    fetchEmbedToken();
  }, []);

  const fetchEmbedSettings = async () => {
    try {
      const response = await settingsService.getEmbedSettings();
      if (response.data.success) {
        const settings = response.data.settings;
        setEmbedEnabled(settings.embed_enabled === '1');
        setMyLearningTypeFilter(settings.my_learning_type_filter === '1');
        setMyLearningSortingFilter(settings.my_learning_sorting_filter === '1');
        setMyLearningGridListButtons(settings.my_learning_grid_list_buttons === '1');
        setCatalogTypeFilter(settings.catalog_type_filter === '1');
        setCatalogSortingFilter(settings.catalog_sorting_filter === '1');
        setCatalogGridListButtons(settings.catalog_grid_list_buttons === '1');
      }
    } catch (error) {
      console.error('Error fetching embed settings:', error);
    }
  };

  const fetchEmbedToken = async () => {
    try {
      const response = await settingsService.getEmbedToken();
      if (response.data.success) {
        setEmbedToken(response.data.token);
        const domains = response.data.allowedDomains || [];
        setAllowedDomain(domains.length > 0 ? domains[0] : '');
      }
    } catch (error) {
      console.error('Error fetching embed token:', error);
    }
  };

  // Combined save function that handles both settings and token
  const handleSaveAll = async () => {
    try {
      setLoading(true);

      // First save the embed settings
      const settingsResponse = await settingsService.updateEmbedSettings({
        embedEnabled,
        myLearningTypeFilter,
        myLearningSortingFilter,
        myLearningGridListButtons,
        catalogTypeFilter,
        catalogSortingFilter,
        catalogGridListButtons
      });

      if (!settingsResponse.data.success) {
        throw new Error('Failed to save embed settings');
      }

      // If embed is enabled, handle token generation/update
      if (embedEnabled) {
        if (!allowedDomain.trim()) {
          showToast('Domain is required when embed is enabled', 'error');
          return;
        }

        if (embedToken) {
          // Update existing token domain
          const tokenResponse = await settingsService.updateEmbedToken({
            allowedDomains: [allowedDomain.trim()]
          });
          if (!tokenResponse.data.success) {
            throw new Error('Failed to update token domain');
          }
        } else {
          // Generate new token
          const tokenResponse = await settingsService.generateEmbedToken({
            allowedDomains: [allowedDomain.trim()]
          });
          if (tokenResponse.data.success) {
            setEmbedToken(tokenResponse.data.token);
          } else {
            throw new Error('Failed to generate embed token');
          }
        }
      } else if (embedToken) {
        // If embed is disabled but token exists, revoke it
        await settingsService.revokeEmbedToken();
        setEmbedToken(null);
        setAllowedDomain('');
      }

      showToast('Embed settings saved successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to save embed settings', 'error');
      console.error('Error saving embed settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeToken = async () => {
    try {
      setLoading(true);
      const response = await settingsService.revokeEmbedToken();
      if (response.data.success) {
        setEmbedToken(null);
        setAllowedDomain('');
        showToast('Embed token revoked successfully', 'success');
      }
    } catch (error) {
      showToast('Failed to revoke embed token', 'error');
    } finally {
      setLoading(false);
    }
  };

  const generateEmbedCode = () => {
    const baseUrl = window.location.origin;
    return `<!-- Beetsol LMS Embed -->
<script>
    window.BEETSOL_EMBED_TOKEN = '${embedToken}';
</script>
<div id="beetsol-lms-container"></div>
<script src="${baseUrl}/beetsol-embed.js"></script>`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('Embed code copied to clipboard!', 'success');
    });
  };

    const copyTokenOnly = () => {
        navigator.clipboard.writeText(embedToken).then(() => {
            showToast('Token copied to clipboard!', 'success');
        }).catch(() => {
            showToast('Failed to copy token', 'error');
        });
    };

    // ── Section header ────────────────────────────────────────────────────────────
    const SectionHeader = ({ title, subtitle }) => (
      <Box sx={{ marginBottom: '20px' }}>
        <Typography sx={{ fontSize: T.font.size.lg, fontWeight: T.font.weight.medium, color: T.color.textPrimary, fontFamily: T.font.family, marginBottom: '4px' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: T.font.size.md, color: T.color.textSecondary, fontFamily: T.font.family }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    );

  return (
    <Box sx={{ p: 2 }}>
      <PageTabs tabs={SETTINGS_TABS} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" gutterBottom className='page-title'>
          Embed Settings
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSaveAll}
          disabled={loading}
        >
          Save All Settings
        </Button>
      </Box>

      {/* Main Embed Toggle */}
      <Paper sx={{ p: 2, mb: 2, pt: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={embedEnabled}
              onChange={(e) => setEmbedEnabled(e.target.checked)}
              color="primary"
            />
          }
          label="Enable Embed Feature"
        />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          When enabled, users can embed your LMS into their websites using a secure token.
        </Typography>
      </Paper>

      {/* Domain Configuration - Show when embed is enabled */}
      {embedEnabled && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
            Domain Security
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Specify the domain that is allowed to use the embed feature. This is mandatory for security.
          </Typography>

          <TextField
            fullWidth
            label="Allowed Domain"
            placeholder="e.g., example.com, *.example.com"
            value={allowedDomain}
            onChange={(e) => setAllowedDomain(e.target.value)}
            required
            error={embedEnabled && !allowedDomain.trim()}
            helperText={
              embedEnabled && !allowedDomain.trim() 
                ? "Domain is required when embed is enabled" 
                : "Enter the domain that can use this embed."
            }
            sx={{ mb: 2 }}
          />          

            {/* Token Status */}
            {embedToken && (
                <>
                    {/* Token Display and Copy Section */}
                    <Box>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'flex-start', md: 'center' },
                            gap: { xs: 1, md: 2 },
                            mb: 1 
                        }}>
                            <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: 'auto' }}>
                                Token:
                            </Typography>
                        
                            <Typography 
                            variant="body2" 
                            sx={{fontSize: '0.75rem',
                                wordBreak: 'break-all',                                
                                p: 1,
                                borderRadius: 0.5,                                
                            }}
                            >
                            {embedToken}
                            </Typography>
                            <Tooltip title="Copy token only">
                                <IconButton
                                    size="small"
                                    onClick={() => copyTokenOnly()}
                                    color="primary"
                                    sx={{ flexShrink: 0 }} // Prevents button from shrinking
                                >
                                    <ContentCopy fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>                          
                    </Box>
                    <Alert severity="success" sx={{ mb: 2}}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <Typography variant="body2">
                                Embed token is active for domain: <StatusPill status={'Active'} label={allowedDomain} />
                            </Typography>
                            <Button
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => setShowTokenDialog(true)}
                            >
                                Revoke
                            </Button>
                        </Box>
                    </Alert>
                </>
            )}
        </Paper>
      )}

      

        {/* Embed Code Display */}
        {embedEnabled && embedToken && allowedDomain && (
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Generated Embed Code
                    </Typography>
                    <Tooltip title="Copy to clipboard">
                        <IconButton
                            onClick={() => copyToClipboard(generateEmbedCode())}
                            color="primary"
                        >
                        <ContentCopy />
                    </IconButton>
                    </Tooltip>
                </Box>
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    This embed will only work on the allowed domain.
                </Typography>
                
                <CodeBlock>
                    {generateEmbedCode()}
                </CodeBlock>

                <Alert severity="info" sx={{ mt: 2}}>
                    The embed will automatically adjust its size and appearance. The token ensures secure access from your specified domain only.
                </Alert>
            </Paper>
        )}

        {/* Status Messages */}
        {!embedEnabled && (
            <Alert severity="info" sx={{ mb: 2}}>
                Enable the embed feature to configure domain security and generate embed code.
            </Alert>
        )}

        {embedEnabled && !allowedDomain && (
            <Alert severity="info"  sx={{ mb: 2}}>
                Please specify an allowed domain to generate the embed code.
            </Alert>
        )}

        {/* Feature Toggles */}
        
            {/*
            <Typography variant="h6" gutterBottom>
                Embed Feature Controls
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Control which features are visible in the embedded LMS interface.
            </Typography>
            */}
            <Divider sx={{ margin: '24px 0', borderColor: T.color.borderTertiary }} />
            <Box>               

                <SectionHeader title="How it works" />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                        ['1', 'Enable Embedding in your workspace settings'],
                        ['2', 'Whitelist Your Domain to allow widget usage on specific websites.'],
                        ['3', 'Generate a Secure Token for authentication.'],
                        ['4', 'Add the Generated Script to your website\'s HTML'],
                        ['5', 'Publish and Use the widget on your approved domain.'],
                    ].map(([num, text]) => (
                    <Box key={num} sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <Box sx={{ width: '22px', height: '22px', borderRadius: '50%', background: T.color.bgSecondary, border: `0.5px solid ${T.color.borderTertiary}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: T.font.weight.medium, color: T.color.textSecondary, flexShrink: 0, fontFamily: T.font.family }}>
                        {num}
                        </Box>
                        <Typography sx={{ fontSize: T.font.size.md, color: T.color.textSecondary, fontFamily: T.font.family, lineHeight: 1.6 }}>
                        {text}
                        </Typography>
                    </Box>
                    ))}
                </Box>
                {/* My Learning Controls 
                <Box>
                    <Typography variant="subtitle2" gutterBottom>My Learning Section</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FormControlLabel
                        control={<Switch checked={myLearningTypeFilter} onChange={(e) => setMyLearningTypeFilter(e.target.checked)} />}
                        label="Type Filter"
                        />
                        <FormControlLabel
                        control={<Switch checked={myLearningSortingFilter} onChange={(e) => setMyLearningSortingFilter(e.target.checked)} />}
                        label="Sorting Filter"
                        />
                        <FormControlLabel
                        control={<Switch checked={myLearningGridListButtons} onChange={(e) => setMyLearningGridListButtons(e.target.checked)} />}
                        label="Grid/List Toggle"
                        />
                    </Box>
                </Box>
            
                
                <Box>
                    <Typography variant="subtitle2" gutterBottom>Catalog Section</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FormControlLabel
                        control={<Switch checked={catalogTypeFilter} onChange={(e) => setCatalogTypeFilter(e.target.checked)} />}
                        label="Type Filter"
                        />
                        <FormControlLabel
                        control={<Switch checked={catalogSortingFilter} onChange={(e) => setCatalogSortingFilter(e.target.checked)} />}
                        label="Sorting Filter"
                        />
                        <FormControlLabel
                        control={<Switch checked={catalogGridListButtons} onChange={(e) => setCatalogGridListButtons(e.target.checked)} />}
                        label="Grid/List Toggle"
                        />
                    </Box>    
                </Box>
                */}
            </Box>
        

      

      {/* Revoke Token Confirmation Dialog */}
      <Dialog open={showTokenDialog} onClose={() => setShowTokenDialog(false)}>
        <DialogTitle>Revoke Embed Token</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revoke the embed token? This will immediately disable all existing embed codes using this token.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTokenDialog(false)}>Cancel</Button>
          <Button onClick={handleRevokeToken} color="error" variant="contained">
            Revoke Token
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmbedSettings;